/**
 * Aim geometry (UI-001).
 *
 * ⚠️ The most important rule in this file is what it does NOT do. `simulateShot`
 * returns the entire outcome of a shot before a single frame is drawn, so the answer
 * to "does this go in?" is sitting in memory the whole time the player is aiming.
 * Reading it would be cheating the player out of the game (ARCHITECTURE.md §5.1).
 *
 * So the guide is computed HERE, from positions and table geometry alone: a ray from
 * the cue ball, stopped at the first thing it would touch. It knows nothing about
 * spin, throw, cushion restitution or what happens next. It is a straightedge, not a
 * prediction — which is also what makes the game a game.
 *
 * Nothing in this file may import the simulation result type, and `firstContact` takes
 * ball positions, never a `SimResult`.
 *
 * Coordinates are table metres throughout (origin bottom-left, +x along the long
 * axis), matching `sim`. Trigonometry is fine here — the determinism rules bind
 * `packages/sim`, and a shot leaves this file as a unit vector rounded to 1e-6, which
 * replays identically everywhere however it was computed (ARCHITECTURE.md §5.5).
 */
import { CUE_BALL, type BallId, type BallSnapshot, type TableGeometry } from '@potted/sim';

export type ContactKind =
  /** The ray reaches an object ball. */
  | 'ball'
  /** The ray reaches a cushion face. */
  | 'cushion'
  /** Nothing within the table — the line runs into a pocket mouth. */
  | 'open';

export interface AimTarget {
  readonly kind: ContactKind;
  /** Cue ball centre, where the line starts. */
  readonly fromXM: number;
  readonly fromYM: number;
  /**
   * Cue ball centre AT first contact — i.e. the centre of the ghost ball. Not the
   * contact point itself: a player aims the ball, not the touching surfaces.
   */
  readonly contactXM: number;
  readonly contactYM: number;
  /** Centre of the object ball that would be struck, if any. */
  readonly objectXM: number | undefined;
  readonly objectYM: number | undefined;
  readonly ballId: BallId | undefined;
  /** Distance from cue centre to ghost centre, metres. */
  readonly distanceM: number;
}

/**
 * Distance at which the ray leaves the playing surface.
 *
 * Used when nothing is struck, which happens when the line runs into a pocket mouth —
 * the rails stop short there, so there is genuinely nothing in the way. Clipping at
 * the boundary rather than running on for ever is what makes the guide read as
 * "into that pocket".
 */
function distanceToEdge(cx: number, cy: number, dx: number, dy: number, geometry: TableGeometry) {
  const { widthM, heightM } = geometry.spec;
  let best = Number.POSITIVE_INFINITY;

  const axis = (position: number, delta: number, limit: number): void => {
    if (delta === 0) return;
    const t = (delta > 0 ? limit - position : -position) / delta;
    if (t >= 0 && t < best) best = t;
  };

  axis(cx, dx, widthM);
  axis(cy, dy, heightM);
  // A direction of (0, 0) never reaches this function, but be safe about the bound.
  return Number.isFinite(best) ? best : Math.sqrt(widthM * widthM + heightM * heightM);
}

/**
 * Earliest distance at which the cue ball, travelling from `(cx, cy)` along the unit
 * vector `(dx, dy)`, would touch object ball `i`.
 *
 * Centre-to-centre contact happens at 2R, so this is a ray against a circle of radius
 * 2R about the object ball — the standard ghost-ball construction.
 */
function distanceToBall(
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  bx: number,
  by: number,
  radiusM: number,
): number | undefined {
  const fx = cx - bx;
  const fy = cy - by;
  const diameter = radiusM * 2;

  const b = fx * dx + fy * dy;
  const c = fx * fx + fy * fy - diameter * diameter;
  const discriminant = b * b - c;
  if (discriminant < 0) return undefined;

  const root = Math.sqrt(discriminant);
  const near = -b - root;
  const far = -b + root;
  // `near < 0 <= far` means the balls already overlap, which a layout never produces.
  const t = near >= 0 ? near : far;
  return t >= 0 ? t : undefined;
}

/**
 * Earliest distance at which the cue ball would touch a cushion face.
 *
 * The ball touches when its centre is R from the cushion's line, so this is a ray
 * against the face offset R into the table. The contact is only real if it lands
 * between the segment's ends — otherwise the ball passes the segment, which is exactly
 * what happens at a pocket mouth, where the rails stop and the gap begins.
 *
 * Contact with a rounded jaw tip is deliberately not modelled: a guide that stopped on
 * a tip would suggest a certainty the physics does not have, and PHY-005 exists
 * precisely because what happens at a jaw is not simple.
 */
function distanceToCushion(
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  from: { readonly xM: number; readonly yM: number },
  to: { readonly xM: number; readonly yM: number },
  nx: number,
  ny: number,
  radiusM: number,
): number | undefined {
  const closing = nx * dx + ny * dy;
  // Parallel, or heading away from this face.
  if (closing >= 0) return undefined;

  const gap = nx * (cx - from.xM) + ny * (cy - from.yM);
  const t = (radiusM - gap) / closing;
  if (t < 0) return undefined;

  // Foot of the contact on the cushion face itself, then its position along the face.
  const footX = cx + dx * t - nx * radiusM;
  const footY = cy + dy * t - ny * radiusM;
  const ex = to.xM - from.xM;
  const ey = to.yM - from.yM;
  const lengthSquared = ex * ex + ey * ey;
  if (lengthSquared === 0) return undefined;

  const along = ((footX - from.xM) * ex + (footY - from.yM) * ey) / lengthSquared;
  return along >= 0 && along <= 1 ? t : undefined;
}

/**
 * The first thing the cue ball would touch, aimed along the unit vector `(dx, dy)`.
 *
 * `balls` is the table as it stands right now; potted balls are ignored. Returns
 * `undefined` only when the cue ball itself is off the table.
 */
export function firstContact(
  balls: readonly BallSnapshot[],
  dx: number,
  dy: number,
  geometry: TableGeometry,
  radiusM: number,
): AimTarget | undefined {
  const cue = balls.find((ball) => ball.id === CUE_BALL);
  if (cue === undefined || cue.potted) return undefined;

  let best = distanceToEdge(cue.xM, cue.yM, dx, dy, geometry);
  let kind: ContactKind = 'open';
  let hit: BallSnapshot | undefined;

  for (const ball of balls) {
    if (ball.id === CUE_BALL || ball.potted) continue;
    const t = distanceToBall(cue.xM, cue.yM, dx, dy, ball.xM, ball.yM, radiusM);
    if (t !== undefined && t < best) {
      best = t;
      kind = 'ball';
      hit = ball;
    }
  }

  for (const cushion of geometry.cushions) {
    const t = distanceToCushion(
      cue.xM,
      cue.yM,
      dx,
      dy,
      cushion.from,
      cushion.to,
      cushion.nx,
      cushion.ny,
      radiusM,
    );
    if (t !== undefined && t < best) {
      best = t;
      kind = 'cushion';
      hit = undefined;
    }
  }

  return {
    kind,
    fromXM: cue.xM,
    fromYM: cue.yM,
    contactXM: cue.xM + dx * best,
    contactYM: cue.yM + dy * best,
    objectXM: hit?.xM,
    objectYM: hit?.yM,
    ballId: hit?.id,
    distanceM: best,
  };
}
